<template>
  <v-app>
    <ToastNotification />
    <v-app-bar flat color="primary">
      <PcsLogoWhite id="pcs-logo-white" />
      <template v-if="isSerialSupported">
        <v-spacer />
        <v-btn
          href="https://www.pressurecontrolsolutions.com/products/electronic-pressure-controllers/eq-da-series-for-ultra-precise-pressure-control/"
          target="_blank"
        >
          <v-icon left class="mr-1">menu_book</v-icon> User Manual
        </v-btn>
        <v-btn
          v-if="showFirmwareDownloadButton"
          @click="downloadFirmware"
          :loading="isDownloadingFirmware"
        >
          <v-icon left class="mr-1">download</v-icon> Firmware
        </v-btn>
        <LoginDialog />
        <ConnectWidget text />
        <v-btn icon>
          <v-icon icon="invert_colors" @click="toggleTheme"></v-icon>
        </v-btn>
      </template>
    </v-app-bar>
    <v-navigation-drawer v-if="isSerialSupported" :rail="isCollapsed" permanent>
      <v-list nav dense color="primary">
        <v-item-group>
          <v-list-item
            v-for="route in routes"
            :key="route.title"
            :title="route.title"
            link
            :to="route.route"
            :prepend-icon="route.icon"
          />
        </v-item-group>
      </v-list>
      <template #append>
        <v-list-item
          prepend-icon="menu"
          title="Toggle Menu"
          @click="isCollapsed = !isCollapsed"
        />
      </template>
    </v-navigation-drawer>
    <v-dialog v-model="showFirmWareErrorDialog" width="30vw">
      <v-card>
        <v-card-title>
          <span class="text-h5">Firmware Download</span>
        </v-card-title>
        <v-card-text>
          <span class="d-flex flex-row">
            <v-icon>error</v-icon>
            <span class="ml-2 mb-3">{{ firmwareErrorMessage }}</span>
          </span>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-main v-if="isSerialSupported" id="main_div" style="padding-top: 0">
      <v-alert
        v-if="isDisconnected"
        :value="true"
        transition="fade"
        type="warning"
        prominent
        class="mt-2"
      >
        No EQ-DA device is found, please connect to an EQ-DA by pressing the
        <ConnectWidget color="primary" /> button.
      </v-alert>
      <router-view />
    </v-main>
    <v-main v-else-if="isHttpsOrLocalhost">
      <!-- the lack of support is not due to the protocol -->
      <h2>
        <v-icon large>usb</v-icon> Serial API not supported by this browser
      </h2>
      <p>
        Please download a Serial API enabled browser:
        <a href="https://www.google.com/chrome">Google Chrome</a>,
        <a href="https://www.chromium.org/">Chromium</a> or
        <a href="https://www.microsoft.com/edge">Microsoft Edge</a>.
      </p>
    </v-main>
    <v-main v-else>
      <!-- the lack of support is due to the protocol -->
      <h2><v-icon large>usb</v-icon> Serial API not available</h2>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useTheme } from "vuetify";
import PcsLogoWhite from "@/assets/pcs_logo_wit.svg";
import ConnectWidget from "@/components/ConnectWidget.vue";
import LoginDialog from "@/components/LoginDialog.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useApiVersion } from "@/composables/useApiVersion";
import { useEventBus } from "@/composables/useEventBus";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { useToast } from "@/composables/useToast";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { AccessLevel } from "@/enums/AccessLevel";
import { saveAs } from "file-saver";

const { apiVersion: parameterVersion } = useApiVersion();
const { on, off } = useEventBus();
const { warning } = useToast();

const {
  downloadFirmware,
  firmwareErrorMessage,
  isDownloadingFirmware,
  showFirmwareDownloadButton,
  showFirmWareErrorDialog,
} = useFirmwareDownload();

const { modbusAddress } = useModbusAddress();
const modbusStore = useModbusStore();
const theme = useTheme();

const nrOfIdStringRegisters = 4;
const isSerialSupported = "serial" in navigator;
const isHttpsOrLocalhost =
  document.location.protocol === "https:" ||
  document.location.hostname === "localhost";

// These particular addresses are invariable, so it doesn't
// matter if 'modbusAddress' uses an old address set.
const {
  softwareVersion: softwareVersionAddr,
  hardwareVersion: hardwareVersionAddr,
  parameterVersion: parameterVersionAddr,
  serialNumber: serialNumberAddr,
  serialNumberPostfix: serialNumberPostfixAddr,
} = modbusAddress;

const routes: Array<{ route: string; title: string; icon: string }> = [
  { route: "/dashboard", title: "Dashboard", icon: "computer" },
  { route: "/charts", title: "Charts", icon: "timeline" },
  { route: "/module-settings", title: "Device Settings", icon: "tune" },
  { route: "/history-data", title: "History Data", icon: "av_timer" },
  { route: "/unit-calculator", title: "Unit Calculator", icon: "calculate" },
  { route: "/about", title: "About", icon: "info" },
];

const isCollapsed = ref(true);

const isDisconnected = computed<boolean>(
  () => modbusStore.portState === PortState.disconnected,
);

const idString = ref<string>("");

function useFirmwareDownload() {
  const showFirmwareDownloadButton = computed(
    () =>
      modbusStore.portState === PortState.connected &&
      [AccessLevel.full, AccessLevel.manufacturer].includes(
        modbusStore.accessLevel,
      ),
  );
  const firmwareVersionsFile = computed(
    () => `${idString.value}_latest_firmware.txt`,
  );
  const isDownloadingFirmware = ref(false);
  const showFirmWareErrorDialog = ref(false);
  const firmwareErrorMessage = ref("");

  async function downloadFirmware() {
    isDownloadingFirmware.value = true;
    try {
      // First, fetch the text file containing the firmware filename
      const response = await fetch(`./data/${firmwareVersionsFile.value}`);
      if (
        !response.ok ||
        response.headers.get("content-type")?.includes("text/html")
      ) {
        firmwareErrorMessage.value = "Failed to fetch firmware version file.";
        showFirmWareErrorDialog.value = true;
        return;
      }

      // Read the filename from the text file
      const firmwareFilename = (await response.text()).trim();

      // Now fetch the actual firmware file
      const firmwareResponse = await fetch(`./data/${firmwareFilename}`);
      if (!firmwareResponse.ok) {
        firmwareErrorMessage.value = "Failed to download firmware file.";
        showFirmWareErrorDialog.value = true;
        return;
      }

      // Save the firmware file
      const firmwareBlob = await firmwareResponse.blob();

      // Check the content to detect SPA fallback (returns index.html instead of the file).
      // Can't rely on content-type because nginx serves .hex as text/html.
      const firstBytes = await firmwareBlob.slice(0, 16).text();
      if (firstBytes.trimStart().startsWith("<")) {
        firmwareErrorMessage.value = "Failed to download firmware file.";
        showFirmWareErrorDialog.value = true;
        return;
      }

      saveAs(firmwareBlob, firmwareFilename);
    } finally {
      isDownloadingFirmware.value = false;
    }
  }
  return {
    downloadFirmware,
    firmwareErrorMessage,
    isDownloadingFirmware,
    showFirmwareDownloadButton,
    showFirmWareErrorDialog,
  };
}

async function reload() {
  if (!modbusStore.storeInit) {
    return;
  }
  const deviceAddress = modbusStore.deviceAddress;
  switch (modbusStore.portState) {
    case PortState.connected:
      await modbusStore.modbusRead(deviceAddress, parameterVersionAddr, 2);
      modbusStore.modbusRead(deviceAddress, softwareVersionAddr, 2);
      modbusStore.modbusRead(deviceAddress, hardwareVersionAddr, 2);
      modbusStore.modbusRead(deviceAddress, serialNumberAddr, 2);
      modbusStore.modbusRead(deviceAddress, serialNumberPostfixAddr, 2);
      break;
    case PortState.disconnected:
      // Clear the device identification first, because setting the parameterVersion
      // will trigger reading the parameters file again.
      const identificationAddr = modbusAddress.identification;
      modbusStore.saveModbusValue([identificationAddr, 0, 0, 0, 0, 0, 0, 0, 0]);
      modbusStore.saveModbusValue([parameterVersionAddr, 0, 0, 0, 0]);
      modbusStore.saveModbusValue([softwareVersionAddr, 0, 0, 0, 0]);
      modbusStore.saveModbusValue([hardwareVersionAddr, 0, 0, 0, 0]);
      modbusStore.saveModbusValue([serialNumberAddr, 0, 0, 0, 0]);
      break;
    case PortState.connecting:
    default:
      // we only receive 'connected' or 'disconnected' events
      break;
  }
}

function toggleTheme() {
  theme.global.name.value = theme.global.current.value.dark ? "light" : "dark";
}

watch(parameterVersion, async (newParameterVersion) => {
  // We cannot fetch the identification address before this point, because the
  // parameter version (and consequently, which address set to use) is known only now.
  const identificationAddr = modbusAddress.identification;
  await modbusStore.modbusRead(
    modbusStore.deviceAddress,
    identificationAddr,
    nrOfIdStringRegisters,
  );
  idString.value = String(
    modbusStore.modbusAddrValue[identificationAddr] ?? "",
  );

  await modbusStore.initLoadParams(newParameterVersion, idString.value.trim());
  await modbusStore.initGroups();
  await modbusStore.initWriteableParams();
});

let accessLevelCheckInterval: ReturnType<typeof setInterval> | null = null;
let previousAccessLevel: AccessLevel | null = null;

function checkAccessLevel() {
  previousAccessLevel = modbusStore.accessLevel;
  modbusStore.modbusRead(
    modbusStore.deviceAddress,
    modbusAddress.accessLevel,
    1,
  );
}

function startInactivityWatch() {
  if (accessLevelCheckInterval) clearInterval(accessLevelCheckInterval);
  accessLevelCheckInterval = setInterval(checkAccessLevel, 60_000); // check every 60 seconds
  checkAccessLevel(); // check immediately
}

function stopInactivityWatch() {
  if (accessLevelCheckInterval) {
    clearInterval(accessLevelCheckInterval);
    accessLevelCheckInterval = null;
  }
}

watch(
  () => modbusStore.accessLevel,
  async (newLevel) => {
    if (newLevel !== AccessLevel.normal) {
      startInactivityWatch();
    } else {
      stopInactivityWatch();
      // When accesslevel been restored to normal
      if (
        previousAccessLevel !== null &&
        previousAccessLevel !== AccessLevel.normal
      ) {
        warning(
          "Your access level has been reset to normal due to inactivity.",
          5000,
        );
      }
    }
    await reload();
    await modbusStore.initWriteableParams();
  },
);

onMounted(() => {
  modbusStore.initLoad();
  on("connected", reload);
  on("disconnected", reload);
});

onUnmounted(() => {
  off("connected", reload);
  off("disconnected", reload);
  stopInactivityWatch();
});
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}

#pcs-logo-white {
  height: 80% !important;
  width: auto;
  margin-left: 8px;
}

/* Note: global, not scoped */
.v-btn {
  text-transform: none !important;
}
</style>
